/**
 * AddressLabelOverrideControl — UI-only display label for headers/exports.
 * Provider queries and data calls continue to use the boundary-derived
 * canonical address (`site.address`).
 */
import React from 'react';
import { Check, Loader2, MapPin, Pencil, X } from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import type { SiteEntity } from '../../types/site';

export interface AddressLabelOverrideControlProps {
  /** Canonical boundary-derived address (read-only context). */
  canonicalAddress: string;
  /** Current override, if any. */
  addressLabel?: string;
  /** Persist override or clear when empty. */
  onSave: (label: string | undefined) => Promise<void>;
  /** `card` = bordered panel; `inline` = compact row for map drawer. */
  variant?: 'card' | 'inline';
  testIdPrefix?: string;
}

export const AddressLabelOverrideControl: React.FC<AddressLabelOverrideControlProps> = ({
  canonicalAddress,
  addressLabel,
  onSave,
  variant = 'inline',
  testIdPrefix = 'address-label',
}) => {
  const [editing, setEditing] = React.useState(false);
  const [draft, setDraft] = React.useState(addressLabel ?? '');
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    if (!editing) setDraft(addressLabel ?? '');
  }, [addressLabel, editing]);

  const hasOverride = !!addressLabel?.trim();
  const canonical = canonicalAddress || '—';

  async function save() {
    const trimmed = draft.trim();
    setSaving(true);
    try {
      await onSave(trimmed || undefined);
      toast.success(
        trimmed
          ? `Display label set to "${trimmed}".`
          : 'Display label cleared — showing canonical address.',
      );
      setEditing(false);
    } catch (e) {
      toast.error(`Failed to update display label: ${(e as Error)?.message ?? 'unknown error'}`);
    } finally {
      setSaving(false);
    }
  }

  const shellClass =
    variant === 'card'
      ? 'border border-border-subtle bg-state-hover/30 p-3'
      : 'border border-border-subtle bg-state-hover/30 px-3 py-2';

  return (
    <div className={shellClass} data-testid={`${testIdPrefix}-override`}>
      <div className="flex items-start gap-2">
        <MapPin className="w-3.5 h-3.5 text-text-secondary mt-0.5 shrink-0" aria-hidden />
        <div className="flex-1 min-w-0">
          {variant === 'card' && (
            <>
              <div className="type-section text-text-secondary mb-0.5">Display label</div>
              <div className="type-meta leading-relaxed mb-2">
                Optional UI-only override for headers and exports. Provider queries
                and data calls still use the boundary-derived canonical address
                <span className="text-text-secondary"> ({canonical})</span>.
              </div>
            </>
          )}
          {variant === 'inline' && (
            <div className="type-meta text-text-muted mb-1.5 leading-relaxed">
              Display label for headers/exports · canonical: {canonical}
            </div>
          )}
          {editing ? (
            <div className="flex items-center gap-2 flex-wrap">
              <input
                type="text"
                value={draft}
                onChange={e => setDraft(e.target.value)}
                placeholder="e.g. Sickel Energy Park"
                maxLength={120}
                disabled={saving}
                className="flex-1 min-w-[10rem] text-[11px] px-2 py-1 border border-border bg-background text-text-primary"
                data-testid={`${testIdPrefix}-input`}
              />
              <Button
                size="sm"
                variant="default"
                disabled={saving}
                onClick={() => void save()}
                className="h-7 rounded-md type-meta"
                data-testid={`${testIdPrefix}-save`}
              >
                {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
              </Button>
              <Button
                size="sm"
                variant="outline"
                disabled={saving}
                onClick={() => { setDraft(addressLabel ?? ''); setEditing(false); }}
                className="h-7 rounded-md type-meta"
                data-testid={`${testIdPrefix}-cancel`}
              >
                <X className="w-3 h-3" />
              </Button>
            </div>
          ) : (
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[11px] text-text-primary truncate" data-testid={`${testIdPrefix}-current`}>
                {hasOverride ? addressLabel : <span className="text-text-muted">No override · using canonical</span>}
              </span>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setEditing(true)}
                className="h-6 rounded-md type-body uppercase tracking-widest ml-auto"
                data-testid={`${testIdPrefix}-edit`}
              >
                <Pencil className="w-2.5 h-2.5 mr-1" /> {hasOverride ? 'Edit' : 'Override'}
              </Button>
              {hasOverride && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => { setDraft(''); void save(); }}
                  disabled={saving}
                  className="h-6 rounded-md type-body uppercase tracking-widest"
                  data-testid={`${testIdPrefix}-clear`}
                >
                  Clear
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/** Site-bound save helper for existing sites. */
export function useSiteAddressLabelSave(
  site: SiteEntity,
  updateSite: (id: string, patch: Partial<SiteEntity>) => Promise<void>,
): (label: string | undefined) => Promise<void> {
  return React.useCallback(
    async (label: string | undefined) => {
      if (!label) {
        await updateSite(site.id, {
          addressLabel: undefined,
          geocodingProvenance: site.addressUpdatedAt ? 'reverse-geocode' : undefined,
        });
      } else {
        await updateSite(site.id, {
          addressLabel: label,
          geocodingProvenance: 'manual-override',
          addressUpdatedAt: new Date().toISOString(),
        });
      }
    },
    [site.id, site.addressUpdatedAt, updateSite],
  );
}
